/**
 * Generate SHA-256 hash using native Web Crypto API
 */
async function generateSHA256Hash(text: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

/**
 * Service for managing slide path mappings using SHA-256 hashes
 */
class SlidePathMappingService {
  private pathToHashMap: Map<string, string> = new Map();
  private hashToPathMap: Map<string, string> = new Map();

  /**
   * Generate SHA-256 hash for a slide path and store the mapping
   */
  async generateHashForPath(slidePath: string): Promise<string> {
    // Check if we already have a hash for this path
    if (this.pathToHashMap.has(slidePath)) {
      return this.pathToHashMap.get(slidePath)!;
    }

    // Generate SHA-256 hash (truncated to 16 chars for shorter URLs)
    const fullHash = await generateSHA256Hash(slidePath);
    const hash = fullHash.substring(0, 16);
    
    // Store bidirectional mapping
    this.pathToHashMap.set(slidePath, hash);
    this.hashToPathMap.set(hash, slidePath);
    
    return hash;
  }

  /**
   * Get the original path from a hash
   */
  getPathFromHash(hash: string): string | null {
    return this.hashToPathMap.get(hash) || null;
  }

  /**
   * Get hash from path (if it exists)
   */
  getHashFromPath(path: string): string | null {
    return this.pathToHashMap.get(path) || null;
  }

  /**
   * Populate mappings from an array of slide paths
   */
  async populateMappings(slidePaths: string[]): Promise<Map<string, string>> {
    const hashToPathMap = new Map<string, string>();
    
    await Promise.all(slidePaths.map(async (path) => {
      const hash = await this.generateHashForPath(path);
      hashToPathMap.set(hash, path);
    }));
    
    return hashToPathMap;
  }

  /**
   * Clear all mappings (useful when switching conversations)
   */
  clearMappings(): void {
    this.pathToHashMap.clear();
    this.hashToPathMap.clear();
  }

  /**
   * Get all current mappings
   */
  getAllMappings(): { pathToHash: Map<string, string>; hashToPath: Map<string, string> } {
    return {
      pathToHash: new Map(this.pathToHashMap),
      hashToPath: new Map(this.hashToPathMap)
    };
  }

  /**
   * Check if a hash exists in the mapping
   */
  hasHash(hash: string): boolean {
    return this.hashToPathMap.has(hash);
  }

  /**
   * Check if a path exists in the mapping
   */
  hasPath(path: string): boolean {
    return this.pathToHashMap.has(path);
  }
}

// Export singleton instance
export const slidePathMapping = new SlidePathMappingService();
export default slidePathMapping;